import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { computed, inject, ref } from 'vue'
import type { Blob as ActiveBlob } from '@rails/activestorage'
import debounce from 'lodash/debounce'

import { SfLog } from '@app/adapters/logger'
import { DocumentLinkType } from '@app/shared/types/sf-data'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type {
  ExternalNoteUploaderItem,
  PreparePageData,
} from '@app/application/client_meetings/types'
import * as ExternalNotesApi from '@app/application/client_meetings/api/external-notes'
import * as MeetingsApi from '@app/application/client_meetings/api/meetings'
import { formatExistingDocument } from '../utils'

const PASTED_NOTES_DEBOUNCE_MS = 2000

type PendingUpload = {
  uuid: string
  document: ExternalNoteUploaderItem
  progress: string
}

export const useExternalNoteStore = defineStore('externalNote', () => {
  const I18n = useI18n()
  const pageData = inject('pageData') as PreparePageData

  const error: Ref<string | null> = ref(null)
  const documents: Ref<ExternalNoteUploaderItem[]> = ref(
    pageData.externalNotes.map(formatExistingDocument)
  )
  const pendingUploads: Ref<PendingUpload[]> = ref([])
  const uploadsSaveFailed: Ref<boolean> = ref(false)
  const maxExternalNotes = pageData.maxExternalNotes

  const pastedNotes: Ref<string> = ref(pageData.pastedNotes)

  const atLimit = computed(
    () =>
      documents.value.length + pendingUploads.value.length >= maxExternalNotes
  )

  function fileInvalid(): void {
    error.value = I18n.t(
      'client_meetings.prepare.external_note_modal.upload.invalid_file'
    )
  }

  function fileAdded({ file, uuid }: { file: File; uuid: string }): void {
    if (atLimit.value) {
      error.value = I18n.t(
        'client_meetings.prepare.external_note_modal.limit_reached',
        { count: maxExternalNotes }
      )
      return
    }
    pendingUploads.value.push({
      uuid,
      progress: '0',
      document: {
        linkType: DocumentLinkType.DirectUpload,
        link: '',
        name: file.name,
        contentType: file.type,
        size: file.size,
        unique_id: uuid,
      },
    })
    error.value = null
  }

  function fileProgress({
    progress,
    uuid,
  }: {
    progress: string
    uuid: string
  }): void {
    const pending = pendingUploads.value.find((p) => p.uuid === uuid)
    if (pending) {
      pending.progress = progress
    }
  }

  function fileError({ uuid }: { uuid: string }): void {
    pendingUploads.value = pendingUploads.value.filter((p) => p.uuid !== uuid)
    error.value = I18n.t('js.attachments.upload_error')
  }

  async function fileUploaded({
    blob,
    uuid,
  }: {
    blob: ActiveBlob
    uuid: string
  }): Promise<void> {
    const pending = pendingUploads.value.find((p) => p.uuid === uuid)
    if (!pending) return
    pending.progress = '100'

    try {
      const response = await ExternalNotesApi.attach(blob.signed_id)
      if (response) {
        documents.value.push(formatExistingDocument(response.document))
        uploadsSaveFailed.value = false
      } else {
        SfLog.error('Got empty response when calling attachExternalNote')
      }
    } catch {
      uploadsSaveFailed.value = true
      error.value = I18n.t(
        'client_meetings.prepare.external_note_modal.attach_error'
      )
    } finally {
      pendingUploads.value = pendingUploads.value.filter((p) => p.uuid !== uuid)
    }
  }

  async function deleteExternalNote(
    document: ExternalNoteUploaderItem
  ): Promise<void> {
    if (document.linkType !== DocumentLinkType.Document) {
      return
    }

    try {
      await ExternalNotesApi.delete(document.document_id)
      documents.value = documents.value.filter(
        (d) => d.unique_id !== document.unique_id
      )
      error.value = null
    } catch {
      error.value = I18n.t(
        'client_meetings.prepare.external_note_modal.delete_error'
      )
    }
  }

  // Latest-write-wins saving: at most one PATCH is in flight, and when it
  // settles with the text changed in the meantime, the next save is
  // rescheduled behind the same debounce. 'saving' covers everything from
  // the first unsaved keystroke until the last save settles.
  const pastedNotesSaveState: Ref<'idle' | 'saving' | 'failed'> = ref('idle')
  let pastedNotesDirty = false
  let pastedNotesSaveInFlight: Promise<void> | null = null

  // Why the process meeting button is blocked on unsettled notes, or null.
  const notesBlockedReason = computed(() => {
    if (
      pendingUploads.value.length > 0 ||
      pastedNotesSaveState.value === 'saving'
    ) {
      return I18n.t('common.saving')
    }
    if (pastedNotesSaveState.value === 'failed') {
      return I18n.t(
        'client_meetings.prepare.external_note_modal.pasted_notes_save_error'
      )
    }
    if (uploadsSaveFailed.value) {
      return I18n.t('client_meetings.prepare.external_note_modal.attach_error')
    }
    return null
  })

  async function runPastedNotesSave(): Promise<void> {
    pastedNotesDirty = false
    try {
      await MeetingsApi.update({
        id: pageData.clientMeetingId,
        pastedNotes: pastedNotes.value,
      })
      if (!pastedNotesDirty) {
        pastedNotesSaveState.value = 'idle'
        error.value = null
      }
    } catch {
      // With newer text queued the rescheduled save sends that instead; only
      // a failure on the latest text is a failure the user must see.
      if (!pastedNotesDirty) {
        pastedNotesSaveState.value = 'failed'
        error.value = I18n.t(
          'client_meetings.prepare.external_note_modal.pasted_notes_save_error'
        )
      }
    } finally {
      if (pastedNotesDirty) {
        debouncedStartPastedNotesSave()
      }
    }
  }

  function startPastedNotesSave(): Promise<void> {
    pastedNotesSaveInFlight ??= runPastedNotesSave().finally(() => {
      pastedNotesSaveInFlight = null
    })
    return pastedNotesSaveInFlight
  }

  const debouncedStartPastedNotesSave = debounce(() => {
    void startPastedNotesSave()
  }, PASTED_NOTES_DEBOUNCE_MS)

  function setPastedNotes(text: string): void {
    if (text === pastedNotes.value) return

    pastedNotes.value = text
    pastedNotesDirty = true
    pastedNotesSaveState.value = 'saving'
    debouncedStartPastedNotesSave()
  }

  // Skips the debounce delay, and resends notes whose last save failed.
  async function flushPastedNotesSave(): Promise<void> {
    if (pastedNotesSaveState.value === 'failed') {
      pastedNotesDirty = true
      pastedNotesSaveState.value = 'saving'
    }

    debouncedStartPastedNotesSave.cancel()

    // Loop: an edit made while a save was in flight leaves the text dirty
    // after that save settles, and the flush must also send it. A failure on
    // the latest text leaves the text clean, so the loop cannot spin on a
    // failing endpoint.
    while (pastedNotesDirty || pastedNotesSaveInFlight) {
      await startPastedNotesSave()
      debouncedStartPastedNotesSave.cancel()
    }
  }

  return {
    documents,
    pendingUploads,
    error,
    atLimit,
    fileInvalid,
    fileAdded,
    fileProgress,
    fileError,
    fileUploaded,
    deleteExternalNote,
    pastedNotes,
    pastedNotesSaveState,
    uploadsSaveFailed,
    notesBlockedReason,
    setPastedNotes,
    flushPastedNotesSave,
  }
})
